import classes from './RelatedProductCard.module.scss'
import classNames from 'classnames'
import ResponsiveImage from '@/components/ResponsiveImage'
import { dataLayerSelectProduct } from '@/utils/dataLayer'
import { useRouter } from 'next/router'
import { useState, useMemo, useCallback } from 'react'
import ReviewStarsWithCounter from '@/components/Review/ReviewStarsWithCounter/ReviewStarsWithCounter'
import useProductReviewsDetails from '@/hooks/useProductReviewsDetails'
import { usePDPDrawerContext } from '@/context/PDPDrawerContext'
import MainProductHeroPricing from '@/components/Sections/MainProductHero/MainProductHeroPricing'
import { useHeadlessCheckoutContext } from '@/context/HeadlessCheckoutContext'
import imageUrlBuilder from '@sanity/image-url'
import sanityClient from '@/services/sanityClient'

import {
  formatShopifyPrice
} from '@/utils/formatPrice'

function RelatedProductCard({
  title,
  images,
  as = 'list-item',
  cardType = 'default',
  product,
  useFlyout,
  usePricing,
  useATC,
  sanityImages,
}) {
  const reviewsInfo = useProductReviewsDetails(product?.handle)
  const { openProductDetailDrawer } = usePDPDrawerContext()
  const { addItemToOrder } = useHeadlessCheckoutContext()

  const mapImage = useCallback(
    (productImage) => {
      const builder = imageUrlBuilder(sanityClient)
      return builder
        .image(productImage.asset.url)
        .width(1000)
        .height(1000)
        .focalPoint(
          productImage?.hotspot?.x || 0.5,
          productImage?.hotspot?.y || 0.5,
        )
        .crop('focalpoint')
        .fit('crop')
        .url()
    },
    [images?.edges?.length, sanityImages?.length],
  )

  const productServingInfo = useCallback(() => {
    const price = product.variants?.[0]?.price;
    const pricingDisplay = product.sanityData?.pricingDisplay;
    const pricingDisplayVariant = 'unit'; // Adjust this based on your logic

    if (!pricingDisplay) return null;

    switch (pricingDisplayVariant) {
      case 'unit':
        if (!pricingDisplay?.quantity) return null;
        return (
          <div
            className={`${classes['product-hero-pricing--servings']} ${classes[pricingDisplayVariant]}`}
          >
            {`${pricingDisplay.quantity} ${pricingDisplay.unit}`}
          </div>
        );
      case 'serving':
        if (!pricingDisplay?.servingsQuantity) return null;
        return (
          <div
            className={`${classes['product-hero-pricing--servings']} ${classes[pricingDisplayVariant]}`}
          >
            <span>{`${pricingDisplay.servingsQuantity} ${pricingDisplay.servingsUnit}`}</span>
            <span>
              (${formatShopifyPrice(price / pricingDisplay.servingsQuantity)}
              /serving)
            </span>
          </div>
        );
      case 'portion':
        if (!pricingDisplay?.portionsQuantity) return null;
        return (
          <div
            className={`${classes['product-hero-pricing--servings']} ${classes[pricingDisplayVariant]}`}
          >
            <span style={{ color: '#ffbb44' }}>
              ${formatShopifyPrice(price / pricingDisplay.portionsQuantity)}
            </span>
            <span style={{ fontSize: '0.6em' }}>per portion</span>
          </div>
        );
      default:
        return null;
    }
  }, [product]);

  const [fistImage] = useMemo(() => {
    if (!sanityImages?.length) {
      return [
        images?.edges[0]?.node.originalSrc
      ]
    }

    return sanityImages
      ?.slice(0, 2)
      .map(({ productImage }) => productImage)
      .map(mapImage)
  }, [images?.edges?.length, sanityImages?.length])

  const [onMoveEvent, setOnMoveEvent] = useState(null)
  const router = useRouter()
  const Element = as === 'list-item' ? 'li' : 'div'

  const flagTag = product?.tags?.find((tag) =>
    tag.toLowerCase()?.includes('flag:')
  )
  const splitFlagTag = flagTag?.split(':')[1]

  const onClickMainButton = async () => {
    // If "useATC" adds the product to cart
    if (useATC) {
      await addItemToOrder({
        product,
        variant: product.variants?.[0],
        openFlyout: true,
      })
      return
    }

    if (useFlyout) {
      openProductDetailDrawer({ productData: product })
    } else {
      dataLayerSelectProduct({ product, url: router.pathname })
      router.push(`/products/${encodeURIComponent(product.content?.handle)}`)
    }
  }

  return (
    <div className={`${classes['wrapper']} wrapper`}>
      {splitFlagTag && (
        <div className={`${classes['best-seller']} best-seller`}>
          {splitFlagTag}
        </div>
      )}
      <Element
        onClick={() => {
          // If flyout is not configured, clicking on the card causes it to open the PDP page
          if (!useATC) {
            onClickMainButton()
          }
        }}
        className={classNames(
          classes['card'],
          classes['accordion-item'],
          cardType === 'hero-main' && classes['card--hero-main'],
          cardType === 'hero-secondary' && classes['card--hero-secondary'],
        )}
        onMouseMove={(event) => setOnMoveEvent(event)}
      >
        {/* <ResponsiveImage src={fistImage} alt={'title'} style={{ objectFit: 'cover' }} /> */}
        <div className={classes['accordion-item__image']}>
          <ResponsiveImage src={fistImage} alt={'title'} />
        </div>

        <div className={classes['card__content']}>
          <div className={classes['title-variant-container']}>
            <h3
              className={classNames(
                classes['card__content__title'],
                cardType === 'hero-secondary' &&
                  classes['card__content__title--centered'],
              )}
            >
              {title}
            </h3>
            {product.variants?.[0] && (
              <div className={classes['card__content__variant']}>
                {productServingInfo({
                  pricingDisplay: true,
                  pricingDisplayVariant: 'unit',
                  quantity: 1,
                  basePrice: product.variants?.[0].price,
                })}
              </div>
            )}
          </div>
          <div className={classes['card__content__pricing']}>
            {usePricing && (
            <MainProductHeroPricing
              product={product}
              selectedVariant={product.variants?.[0]}
              pricingDisplay={false}
            />
          )}
          {cardType !== 'hero-secondary' && reviewsInfo && (
            <ReviewStarsWithCounter
              rating={reviewsInfo.average}
              numberOfReviews={reviewsInfo.totalReviews}
            />
          )}
          <div className={classes['card__button-container']}>
            <button onClick={onClickMainButton} className={classes['card__button']}>
            {useATC ? 'Add to Basket' : 'View Details'}
            </button>
          </div>
         </div>
        </div>
      </Element>
    </div>
  )
}

export default RelatedProductCard
